// src/components/Integrations/Integrations.jsx
import React, { useState, useEffect, lazy, Suspense } from "react";
import { useHistory } from "react-router-dom";
import UpgradePath from "../Utilities/UpgradePath/UpgradePath";

// Lazy-loaded integration components (only those rendered inline)
const LazyStripe = lazy(() => import("./Stripe/Stripe"));
const LazyZoom = lazy(() => import("./Zoom/Zoom"));
const LazyZapier = lazy(() => import("./Zapier/Zapier"));
const LazyWebhooks = lazy(() => import("./Webhooks/Webhooks"));
const LazySalesForza = lazy(() => import("./SalesForza/SalesForza"));
const LazyTelegram = lazy(() => import("./Telegram/Telegram"));
const LazyWhatsapp = lazy(() => import("./Whatsapp/Whatsapp"));

// Valid hash keys
const VALID_KEYS = [
  "whatsapp",
  "stripe",
  "zoom",
  "zapier",
  "webhooks",
  "gmail",
  "telegram",
  "hubspot",
  "apollo",
];

function getInitialKey() {
  const h = window.location.hash.replace("#", "");
  return VALID_KEYS.includes(h) ? h : null;
}

// Loading placeholder
const Loader = () => <div className="tw-text-center tw-p-4">Loading…</div>;

// Data-driven list of integrations
const integrations = [
  {
    key: "whatsapp",
    title: "WhatsApp",
    description: "Send booking reminders and updates instantly via WhatsApp.",
    logo: "/app-assets/logos/whatsapp.jpg",
    component: LazyWhatsapp,
    status: "new",
    enabled: true,
  },
  {
    key: "apollo",
    title: "Apollo",
    description: "Find new leads and turn them into bookings.",
    logo: "/app-assets/logos/apollo.jpg",
    internalPath: "/leads",
    status: "new",
    enabled: true,
  },
  {
    key: "stripe",
    title: "Stripe",
    description: "Accept payments with 0% fee—even on the free plan.",
    logo: "/app-assets/logos/stripe.jpg",
    component: LazyStripe,
    enabled: true,
  },
  {
    key: "zoom",
    title: "Zoom",
    description: "Video conferencing via Zoom.",
    logo: "/app-assets/logos/zoom.jpg",
    component: () => (
      <UpgradePath featureName="zoom" position="right">
        <LazyZoom />
      </UpgradePath>
    ),
    enabled: true,
  },
  {
    key: "zapier",
    title: "Zapier",
    description: "Automate everything with 5000+ Zapier integrations.",
    logo: "/app-assets/logos/zapier.jpg",
    component: () => (
      <UpgradePath featureName="zapier-integration" position="right">
        <LazyZapier />
      </UpgradePath>
    ),
    enabled: true,
  },
  {
    key: "webhooks",
    title: "Webhooks",
    description: "Automate everything with webhooks.",
    logo: "/app-assets/logos/webhook.jpg",
    component: () => (
      <UpgradePath featureName="webhooks" position="right">
        <LazyWebhooks />
      </UpgradePath>
    ),
    enabled: true,
  },
  {
    key: "gmail",
    title: "Gmail",
    description: "Send personalized Gmail notifications with SalesForza.",
    logo: "/app-assets/logos/gmail.jpg",
    component: () => (
      <UpgradePath featureName="salesforza" position="right">
        <LazySalesForza />
      </UpgradePath>
    ),
    enabled: true,
  },
  {
    key: "telegram",
    title: "Telegram",
    description: "Send booking reminders and updates instantly via Telegram.",
    logo: "/app-assets/logos/telegram.jpg",
    component: LazyTelegram,
    enabled: true,
  },
  {
    key: "wix",
    title: "Wix",
    description: "Publish your booking page on Wix.",
    logo: "/app-assets/logos/wix.jpg",
    externalUrl: "https://it.wix.com/app-market/web-solution/calendbook",
    enabled: true,
  },
  {
    key: "hubspot",
    title: "HubSpot",
    description:
      "Keep your leads updated automatically between Calendbook and HubSpot.",
    logo: "/app-assets/logos/hubspot.jpg",
    component: () => (
      <div className="tw-text-gray-700 dark:tw-text-gray-300 tw-p-6 tw-text-lg">
        HubSpot integration is coming soon.
      </div>
    ),
    status: "coming_soon",
    enabled: false,
  },
];

export default function Integrations() {
  const [activeKey, setActiveKey] = useState(getInitialKey);
  const history = useHistory();

  // Sync page title & URL hash
  useEffect(() => {
    if (activeKey) {
      document.title = `Calendbook - Integrations - ${activeKey}`;
      window.history.replaceState(null, "", `#${activeKey}`);
    } else {
      document.title = `Calendbook - Integrations`;
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [activeKey]);

  // Show Tidio chat if available
  useEffect(() => {
    try {
      window.tidioChatApi?.show();
    } catch {}
  }, []);

  const active = integrations.find((i) => i.key === activeKey);

  return (
    <div className="tw-container tw-mx-auto tw-px-4 tw-my-8">
      {/* Cards List & Header */}
      {!active && (
        <>
          <header className="tw-mb-8">
            <h1 className="tw-text-4xl tw-font-bold tw-text-gray-900 dark:tw-text-white">
              Integrations
            </h1>
            <p className="tw-mt-2 tw-text-lg tw-text-gray-600 dark:tw-text-gray-400">
              Connect Calendbook to your favorite services and extend its
              capabilities.
            </p>
          </header>

          <div className="tw-grid tw-gap-6 tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
            {integrations.map((intg) => {
              const isDisabled = intg.enabled === false;
              const handleClick = () => {
                if (isDisabled) return;
                if (intg.externalUrl) {
                  window.open(intg.externalUrl, "_blank");
                } else if (intg.internalPath) {
                  history.push(intg.internalPath);
                } else {
                  setActiveKey(intg.key);
                }
              };

              return (
                <div
                  key={intg.key}
                  onClick={handleClick}
                  className={`tw-relative tw-transform tw-transition-all tw-duration-300
                    tw-rounded-lg tw-p-6 tw-border tw-border-gray-200 dark:tw-border-gray-700
                    tw-bg-white dark:tw-bg-gray-800
                    hover:tw-shadow-lg hover:tw-shadow-primary-500/40 hover:tw-border-primary-500 hover:tw-scale-105
                    ${
                      isDisabled
                        ? "tw-opacity-50 tw-cursor-not-allowed"
                        : "tw-cursor-pointer"
                    }`}
                >
                  {/* Status Badge */}
                  {intg.status && (
                    <span
                      className={`tw-absolute tw-top-3 tw-right-3 tw-px-3 tw-py-1
                        tw-text-xs tw-font-bold tw-uppercase tw-rounded-full tw-shadow-lg
                        ${
                          intg.status === "new"
                            ? "tw-bg-gradient-to-r tw-from-green-400 tw-to-green-600 tw-text-white"
                            : "tw-bg-gradient-to-r tw-from-yellow-400 tw-to-yellow-600 tw-text-white"
                        }`}
                    >
                      {intg.status === "new" ? "New" : "Coming Soon"}
                    </span>
                  )}

                  <img
                    src={intg.logo}
                    alt={`${intg.title} logo`}
                    className="tw-w-10 tw-h-10 tw-mb-3 tw-object-contain tw-rounded-full"
                  />
                  <h5 className="tw-mb-2 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-gray-900 dark:tw-text-white">
                    {intg.title}
                  </h5>
                  <p className="tw-mb-3 tw-font-normal tw-text-gray-500 dark:tw-text-gray-400">
                    {intg.description}
                  </p>
                  <span className="tw-inline-flex tw-items-center tw-text-primary-500 hover:tw-underline">
                    View integration
                    <i className="fa-solid fa-arrow-up-right tw-w-3 tw-h-3 tw-ml-2.5" />
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Active Integration Detail */}
      {active && (
        <div className="tw-mt-8">
          <button
            onClick={() => setActiveKey(null)}
            className="tw-text-primary-500 hover:tw-underline tw-mb-4"
          >
            ← All integrations
          </button>

          <Suspense fallback={<Loader />}>
            {typeof active.component === "function" &&
            String(active.component).includes("return")
              ? active.component()
              : React.createElement(active.component)}
          </Suspense>
        </div>
      )}
    </div>
  );
}
